import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { ChevronDown, ChevronRight, UserCog, Lock, Ban, Link2, HandCoins } from 'lucide-react';
import { AppDispatch } from '../../../configuration';
import { fetchProfile } from '../../../redux/features/auth/slice';
import EditProfiledDialog from '../../../components/edit-profile-dialog/edit-profile-dialog';
import ChangePasswordForm from '../../../components/edit-profile-dialog/edit-change-password';
import BlockList from '../../user-setting/tabs/block-list';
import CustomUrlSetting from '../../user-setting/tabs/custom-url-setting';

/**
 * Trang Cài đặt tài khoản (tab "Cài đặt" ở trang cá nhân).
 * Trước đây tab này vào thẳng cài đặt Donate (chỉ creator mới thấy) → gây khó hiểu.
 * Giờ gộp các mục cài đặt thật: Tài khoản (sửa hồ sơ + đổi mật khẩu), Người bị chặn, kèm lối sang Cài đặt Donate.
 * Dạng accordion cho gọn + hợp mobile.
 */

interface Props {
  profile: any;
}

interface SectionProps {
  icon: React.ReactNode;
  title: string;
  desc?: string;
  open: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}

const Section: React.FC<SectionProps> = ({ icon, title, desc, open, onToggle, children }) => (
  <div className="overflow-hidden rounded-xl border border-[#DEE0E6] bg-white dark:border-slate-700 dark:bg-dark2">
    <button
      type="button"
      onClick={onToggle}
      className="flex w-full items-center gap-3 px-4 py-4 text-left"
    >
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#EAEFFA] text-[#0057FF] dark:bg-slate-700 dark:text-blue-300">
        {icon}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-[15px] font-semibold text-[#19052f] dark:text-white">{title}</span>
        {desc && <span className="block text-[13px] text-slate-500 dark:text-slate-400">{desc}</span>}
      </span>
      <ChevronDown
        size={20}
        className={`shrink-0 text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`}
      />
    </button>
    {open && <div className="border-t border-[#EEF0F4] dark:border-slate-700">{children}</div>}
  </div>
);

const AccountSettings: React.FC<Props> = ({ profile }) => {
  const dispatch = useDispatch<AppDispatch>();
  const [open, setOpen] = useState<string>('account');
  const toggle = (k: string) => setOpen((cur) => (cur === k ? '' : k));

  return (
    <div className="mx-auto flex max-w-[760px] flex-col gap-3">
      {/* Tài khoản */}
      <Section
        icon={<UserCog size={18} />}
        title="Tài khoản"
        desc="Chỉnh sửa hồ sơ, thông tin cá nhân"
        open={open === 'account'}
        onToggle={() => toggle('account')}
      >
        <div className="flex flex-col gap-4 p-4">
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold text-[#19052f] dark:text-white">
              {profile?.fullname || profile?.username || 'Người dùng'}
            </p>
            <p className="truncate text-xs text-slate-500 dark:text-slate-400">{profile?.email}</p>
          </div>
          {/* Nút dùng chung từ EditProfiledDialog: full-width mới đẹp (không có padding ngang) */}
          <EditProfiledDialog profile={profile} onUpdateSuccess={() => dispatch(fetchProfile())} />
        </div>
      </Section>

      {/* Đổi mật khẩu */}
      <Section
        icon={<Lock size={18} />}
        title="Đổi mật khẩu"
        desc="Cập nhật mật khẩu đăng nhập"
        open={open === 'password'}
        onToggle={() => toggle('password')}
      >
        <ChangePasswordForm onClose={() => setOpen('')} />
      </Section>

      {/* Đường dẫn tuỳ chỉnh (custom URL) */}
      <Section
        icon={<Link2 size={18} />}
        title="Đường dẫn tuỳ chỉnh"
        desc="Đặt link riêng cho trang cá nhân (cab.vn/user/...)"
        open={open === 'customurl'}
        onToggle={() => toggle('customurl')}
      >
        <CustomUrlSetting />
      </Section>

      {/* Cài đặt Donate nằm ở trang riêng (/settings). Trước đây không có nút nào dẫn tới đó,
          creator phải tự gõ địa chỉ nên không tìm được chỗ bật các kiểu donate. */}
      <Link
        to="/settings?tab=donate"
        className="flex w-full items-center gap-3 rounded-xl border border-[#DEE0E6] bg-white px-4 py-4 text-left dark:border-slate-700 dark:bg-dark2"
      >
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#EAEFFA] text-[#0057FF] dark:bg-slate-700 dark:text-blue-300">
          <HandCoins size={18} />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block text-[15px] font-semibold text-[#19052f] dark:text-white">Donate và livestream</span>
          <span className="block text-[13px] text-slate-500 dark:text-slate-400">
            Bật các kiểu donate, mục tiêu, bảng xếp hạng và lấy link chèn vào OBS
          </span>
        </span>
        <ChevronRight size={20} className="shrink-0 text-slate-400" />
      </Link>

      {/* Người dùng bị chặn */}
      <Section
        icon={<Ban size={18} />}
        title="Người dùng bị chặn"
        desc="Danh sách tài khoản bạn đã chặn"
        open={open === 'blocked'}
        onToggle={() => toggle('blocked')}
      >
        <div className="p-3">
          <BlockList />
        </div>
      </Section>
    </div>
  );
};

export default AccountSettings;
